/**
 * CREATIVE DETONATOR - Frontend Logger
 * Environment-aware logging with levels and formatting
 *
 * @version 1.0.0
 * @description Implements FRONTEND-25 (Console Logging Cleanup)
 */

// ============================================================================
// CONFIGURATION
// ============================================================================

const LogLevel = {
  NONE: 0,
  ERROR: 1,
  WARN: 2,
  INFO: 3,
  DEBUG: 4,
  TRACE: 5
};

// Determine environment
const isDevelopment = window.location.hostname === 'localhost' ||
                      window.location.hostname === '127.0.0.1';

// Default level based on environment
let currentLevel = isDevelopment ? LogLevel.DEBUG : LogLevel.WARN;

// Allow override via localStorage
try {
  const storedLevel = localStorage.getItem('logLevel');
  if (storedLevel && LogLevel[storedLevel.toUpperCase()] !== undefined) {
    currentLevel = LogLevel[storedLevel.toUpperCase()];
  }
} catch (e) {
  // localStorage not available
}

// ============================================================================
// LOGGER CLASS
// ============================================================================

class Logger {
  constructor(namespace) {
    this.namespace = namespace;
    this.styles = {
      namespace: 'color: #666; font-weight: bold;',
      error: 'color: #ff0000; font-weight: bold;',
      warn: 'color: #ff9900;',
      info: 'color: #0066ff;',
      debug: 'color: #00cc00;',
      trace: 'color: #999;'
    };
  }

  /**
   * Format log message with namespace and timestamp
   * @private
   */
  _format(level, message, ...args) {
    const timestamp = new Date().toISOString().substr(11, 12);
    const prefix = `[${timestamp}] [${this.namespace}]`;
    return [prefix, message, ...args];
  }

  /**
   * Check if level is enabled
   * @private
   */
  _shouldLog(level) {
    return level <= currentLevel;
  }

  /**
   * Error - Always logged in production
   */
  error(message, ...args) {
    if (this._shouldLog(LogLevel.ERROR)) {
      console.error(...this._format('ERROR', message, ...args));
    }
  }

  /**
   * Warning - Logged in production
   */
  warn(message, ...args) {
    if (this._shouldLog(LogLevel.WARN)) {
      console.warn(...this._format('WARN', message, ...args));
    }
  }

  /**
   * Info - General information
   */
  info(message, ...args) {
    if (this._shouldLog(LogLevel.INFO)) {
      console.info(...this._format('INFO', message, ...args));
    }
  }

  /**
   * Debug - Development only
   */
  debug(message, ...args) {
    if (this._shouldLog(LogLevel.DEBUG)) {
      console.log(...this._format('DEBUG', message, ...args));
    }
  }

  /**
   * Trace - Verbose debugging
   */
  trace(message, ...args) {
    if (this._shouldLog(LogLevel.TRACE)) {
      console.log(...this._format('TRACE', message, ...args));
    }
  }

  /**
   * Group related logs
   */
  group(label) {
    if (this._shouldLog(LogLevel.DEBUG)) {
      console.group(`[${this.namespace}] ${label}`);
    }
  }

  groupEnd() {
    if (this._shouldLog(LogLevel.DEBUG)) {
      console.groupEnd();
    }
  }

  /**
   * Collapsed group
   */
  groupCollapsed(label) {
    if (this._shouldLog(LogLevel.DEBUG)) {
      console.groupCollapsed(`[${this.namespace}] ${label}`);
    }
  }

  /**
   * Time measurement
   */
  time(label) {
    if (this._shouldLog(LogLevel.DEBUG)) {
      console.time(`[${this.namespace}] ${label}`);
    }
  }

  timeEnd(label) {
    if (this._shouldLog(LogLevel.DEBUG)) {
      console.timeEnd(`[${this.namespace}] ${label}`);
    }
  }

  /**
   * Table output
   */
  table(data) {
    if (this._shouldLog(LogLevel.DEBUG)) {
      console.table(data);
    }
  }

  /**
   * Assert (logs error if condition is false)
   */
  assert(condition, message, ...args) {
    if (!condition && this._shouldLog(LogLevel.ERROR)) {
      console.assert(condition, ...this._format('ASSERT', message, ...args));
    }
  }

  /**
   * Count occurrences
   */
  count(label = 'default') {
    if (this._shouldLog(LogLevel.DEBUG)) {
      console.count(`[${this.namespace}] ${label}`);
    }
  }

  countReset(label = 'default') {
    if (this._shouldLog(LogLevel.DEBUG)) {
      console.countReset(`[${this.namespace}] ${label}`);
    }
  }
}

// ============================================================================
// FACTORY FUNCTION
// ============================================================================

/**
 * Create namespaced logger
 * @param {string} namespace - Logger namespace (e.g., 'Generator', 'Auth')
 * @returns {Logger} Logger instance
 *
 * @example
 * import { createLogger } from './core/logger.js';
 * const log = createLogger('MyModule');
 * log.debug('Hello', { data: 123 });
 * log.error('Something went wrong!');
 */
export function createLogger(namespace) {
  return new Logger(namespace);
}

// ============================================================================
// LOG LEVEL CONTROL
// ============================================================================

/**
 * Set log level at runtime
 * @param {string|number} level - Level name or number
 */
export function setLogLevel(level) {
  if (typeof level === 'string') {
    level = LogLevel[level.toUpperCase()];
  }
  if (level !== undefined && level >= LogLevel.NONE && level <= LogLevel.TRACE) {
    currentLevel = level;
    try {
      localStorage.setItem('logLevel', Object.keys(LogLevel).find(k => LogLevel[k] === level));
    } catch (e) {
      // localStorage not available
    }
    console.log(`[Logger] Level set to ${Object.keys(LogLevel).find(k => LogLevel[k] === level)}`);
  }
}

/**
 * Get current log level
 * @returns {string} Level name
 */
export function getLogLevel() {
  return Object.keys(LogLevel).find(k => LogLevel[k] === currentLevel);
}

// ============================================================================
// SENSITIVE DATA FILTER
// ============================================================================

const DEFAULT_SENSITIVE_FIELDS = [
  'token', 'password', 'apiKey', 'secret', 'auth',
  'authorization', 'cookie', 'session', 'key', 'credential'
];

/**
 * Sanitize object for logging (remove sensitive fields)
 * @param {any} obj - Object to sanitize
 * @param {string[]} sensitiveFields - Fields to redact
 * @returns {any} Sanitized object safe for logging
 *
 * @example
 * log.debug('User data:', sanitize(userData));
 */
export function sanitize(obj, sensitiveFields = DEFAULT_SENSITIVE_FIELDS) {
  if (!obj || typeof obj !== 'object') return obj;

  const sanitized = Array.isArray(obj) ? [...obj] : { ...obj };

  for (const key of Object.keys(sanitized)) {
    const lowerKey = key.toLowerCase();

    // Check if field name is sensitive
    if (sensitiveFields.some(f => lowerKey.includes(f.toLowerCase()))) {
      sanitized[key] = '[REDACTED]';
    } else if (typeof sanitized[key] === 'object' && sanitized[key] !== null) {
      sanitized[key] = sanitize(sanitized[key], sensitiveFields);
    }
  }

  return sanitized;
}

// ============================================================================
// PERFORMANCE LOGGING
// ============================================================================

/**
 * Measure async function execution time
 * @param {string} label - Label for the measurement
 * @param {Function} fn - Async function to measure
 * @returns {any} Result of the function
 */
export async function measureAsync(label, fn) {
  const start = performance.now();
  try {
    const result = await fn();
    const duration = (performance.now() - start).toFixed(2);
    if (isDevelopment) {
      console.log(`[Perf] ${label}: ${duration}ms`);
    }
    return result;
  } catch (error) {
    const duration = (performance.now() - start).toFixed(2);
    if (isDevelopment) {
      console.log(`[Perf] ${label} (failed): ${duration}ms`);
    }
    throw error;
  }
}

/**
 * Create a performance marker
 */
export function mark(name) {
  if (isDevelopment && performance.mark) {
    performance.mark(name);
  }
}

/**
 * Measure between two marks
 */
export function measure(name, startMark, endMark) {
  if (isDevelopment && performance.measure) {
    try {
      performance.measure(name, startMark, endMark);
      const entries = performance.getEntriesByName(name);
      if (entries.length > 0) {
        console.log(`[Perf] ${name}: ${entries[entries.length - 1].duration.toFixed(2)}ms`);
      }
    } catch (e) {
      // Marks may not exist
    }
  }
}

// ============================================================================
// GLOBAL EXPORT
// ============================================================================

// Default logger instance for window.Logger convenience methods
const defaultLogger = new Logger('app');

window.Logger = {
  // Factory and utilities
  create: createLogger,
  setLevel: setLogLevel,
  getLevel: getLogLevel,
  sanitize,
  measureAsync,
  mark,
  measure,
  LogLevel,

  // Convenience methods that accept namespace as first argument
  // Usage: window.Logger.debug('main', 'message') or window.Logger.error('api', 'error')
  debug: (namespace, ...args) => {
    const log = new Logger(namespace);
    log.debug(...args);
  },
  info: (namespace, ...args) => {
    const log = new Logger(namespace);
    log.info(...args);
  },
  warn: (namespace, ...args) => {
    const log = new Logger(namespace);
    log.warn(...args);
  },
  error: (namespace, ...args) => {
    const log = new Logger(namespace);
    log.error(...args);
  }
};

export { LogLevel };
export default createLogger;
